/**
 * Form controls — label + control in one component, because that is how every
 * page already writes them:
 *
 *   <label><span>Name</span><input className={input} …/></label>
 *
 * ...where `input` is a class string each page redeclares at the top of its
 * file. Eleven copies of the same string is how the panel drifted; this is the
 * one place the field skin lives now.
 *
 *   <Input label="Host (IP)" mono value={…} onChange={…} required />
 *   <Select label="Kind" value={…} onChange={…}><option …/></Select>
 *   <Textarea label="SSH private key" mono rows={5} … />
 *
 * Focus comes from the mockup's search field: --color-blue border plus a 3px
 * --color-s-blue halo. The global :focus-visible ring is suppressed here
 * because the halo already carries it and two rings look like a bug.
 */

const CONTROL = [
  'w-full rounded-ctl border border-bd-strong bg-white px-3 py-2',
  'text-[13.5px] text-t1 placeholder:text-ic-light',
  'focus:outline-none focus:border-blue focus:shadow-[0_0_0_3px_var(--color-s-blue)]',
  'disabled:bg-s-3 disabled:text-t3 disabled:cursor-not-allowed',
].join(' ');

/** Shared label + optional hint wrapper. */
function Wrap({ label, hint, htmlFor, className = '', children }) {
  return (
    <label className={['block', className].join(' ')} htmlFor={htmlFor}>
      {label && (
        <span className="mb-1.5 block text-[13px] font-medium text-t2">{label}</span>
      )}
      {children}
      {hint && <span className="mt-1 block text-[12px] text-t3">{hint}</span>}
    </label>
  );
}

export function Input({ label, hint, mono = false, className = '', ...rest }) {
  return (
    <Wrap label={label} hint={hint} className={className}>
      <input className={[CONTROL, mono ? 'font-mono text-[12.5px]' : ''].join(' ')} {...rest} />
    </Wrap>
  );
}

export function Textarea({ label, hint, mono = false, className = '', rows = 4, ...rest }) {
  return (
    <Wrap label={label} hint={hint} className={className}>
      <textarea
        rows={rows}
        className={[CONTROL, mono ? 'font-mono text-[12.5px]' : '', 'resize-y'].join(' ')}
        {...rest}
      />
    </Wrap>
  );
}

export function Select({ label, hint, className = '', children, ...rest }) {
  return (
    <Wrap label={label} hint={hint} className={className}>
      <select className={CONTROL} {...rest}>{children}</select>
    </Wrap>
  );
}

/**
 * Radio — a labelled dot, for a small mutually-exclusive choice inline (the
 * Add-server form's key-vs-password switch). A real <input type="radio"> keeps
 * arrow-key group navigation, which a styled div would lose.
 */
export function Radio({ label, className = '', ...rest }) {
  return (
    <label className={['inline-flex items-center gap-1.5 cursor-pointer', className].join(' ')}>
      <input type="radio" className="peer sr-only" {...rest} />
      <span
        className={[
          'grid place-items-center w-4 h-4 shrink-0 rounded-full border border-bd-strong bg-white',
          'peer-checked:border-blue peer-checked:border-[5px]',
          'peer-focus-visible:outline-2 peer-focus-visible:outline-blue peer-focus-visible:outline-offset-2',
        ].join(' ')}
        aria-hidden="true"
      />
      <span className="text-[13px] text-t2">{label}</span>
    </label>
  );
}

/**
 * InputGroup — one labelled control built from several joined parts, e.g.
 * `[local part] @ [domain ▾]` or `[host] : [port]`.
 *
 *   <InputGroup label="Mailbox">
 *     <input className="w-32 font-mono" … />
 *     <span>@</span>
 *     <select …>…</select>
 *   </InputGroup>
 *
 * The skin is applied to the direct children from here rather than each child
 * carrying it, so the seams line up and only the outer corners are rounded.
 * Children stay plain `<input>` / `<select>` / `<span>` — wrapping each in its
 * own component would mean four exports for one shape.
 */
export function InputGroup({ label, hint, className = '', children }) {
  return (
    <Wrap label={label} hint={hint} className={className}>
      <div
        className={[
          'flex items-stretch',
          // shared skin for every part
          '[&>input]:border [&>select]:border [&>span]:border',
          '[&>input]:border-bd-strong [&>select]:border-bd-strong [&>span]:border-bd-strong',
          '[&>input]:bg-white [&>select]:bg-white [&>span]:bg-s-2',
          '[&>input]:px-3 [&>select]:px-2 [&>span]:px-2',
          '[&>input]:py-2 [&>select]:py-2 [&>span]:py-2',
          '[&>input]:text-[13.5px] [&>select]:text-[13.5px] [&>span]:text-[13.5px]',
          '[&>input]:text-t1 [&>select]:text-t1 [&>span]:text-t3',
          '[&>span]:grid [&>span]:place-items-center',
          // collapse the seams, round only the ends
          '[&>*:not(:first-child)]:-ml-px',
          '[&>*:first-child]:rounded-l-ctl [&>*:last-child]:rounded-r-ctl',
          // focus lifts the focused part above its neighbours so its ring is whole
          '[&>input:focus]:relative [&>select:focus]:relative',
          '[&>input:focus]:z-10 [&>select:focus]:z-10',
          '[&>input:focus]:outline-none [&>select:focus]:outline-none',
          '[&>input:focus]:border-blue [&>select:focus]:border-blue',
          '[&>input:focus]:shadow-[0_0_0_3px_var(--color-s-blue)]',
          '[&>select:focus]:shadow-[0_0_0_3px_var(--color-s-blue)]',
        ].join(' ')}
      >
        {children}
      </div>
    </Wrap>
  );
}

/**
 * Inline form error. Uses --color-err per §3 — a failed submit is something
 * that broke, not a note.
 */
export function FormError({ children }) {
  if (!children) return null;
  return (
    <p role="alert" className="text-[13px] text-err">
      {children}
    </p>
  );
}

/** Row of labelled radios sharing one group name. */
export function RadioGroup({ label, children, className = '' }) {
  return (
    <div className={['flex items-center gap-4 flex-wrap', className].join(' ')}>
      {label && <span className="text-[13px] font-medium text-t2">{label}</span>}
      {children}
    </div>
  );
}
